import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, Badge, Spinner } from "../components";
import { QueryError } from "../components/QueryError";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";

interface WorkstreamData {
  id: string;
  name: string;
  episodeCount: number;
  avgReward: number;
  rewardTrend: number[];
  activeExperiments: { id: string; name: string; status: string; improvement: number }[];
  vectorConvergence: number;
  contributingTenants: { id: string; name: string; episodes: number }[];
}

export default function WorkstreamDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["workstream-detail", id],
    queryFn: () => apiGet<WorkstreamData>(`/api/learning/workstreams/${id}`),
    enabled: !!id,
  });

  usePolling([["workstream-detail", id ?? ""]], 30_000);

  if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spinner size="md" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <Link
            to="/learning"
            style={{ fontSize: 13, color: "var(--c-text-secondary)", textDecoration: "none" }}
          >
            Learning Dashboard
          </Link>
        </div>
        <QueryError
          error={error instanceof Error ? error : new Error("Failed to load workstream")}
          queryKeys={[["workstream-detail", id ?? ""]]}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Link
          to="/learning"
          style={{ fontSize: 13, color: "var(--c-text-secondary)", textDecoration: "none" }}
        >
          Learning Dashboard
        </Link>
        <span style={{ color: "var(--c-text-faint)", margin: "0 8px" }}>/</span>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{data.name}</span>
      </div>

      <h1 style={{ margin: "0 0 8px", fontSize: 24, fontWeight: 600 }}>{data.name}</h1>
      <p style={{ margin: "0 0 24px", color: "var(--c-text-secondary)", fontSize: 14 }}>
        {data.episodeCount} episodes | Avg reward: {data.avgReward.toFixed(3)} | Convergence:{" "}
        {(data.vectorConvergence * 100).toFixed(1)}%
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Reward Trend */}
        <Card style={{ padding: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600 }}>Reward Trend</h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 80 }}>
            {data.rewardTrend.map((value, i) => {
              const height = Math.max(4, value * 80);
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height,
                    backgroundColor:
                      value >= data.avgReward
                        ? "var(--c-success, #22c55e)"
                        : "var(--c-warning, #f59e0b)",
                    borderRadius: 2,
                    minWidth: 3,
                  }}
                  title={`${value.toFixed(3)}`}
                />
              );
            })}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 8,
              fontSize: 11,
              color: "var(--c-text-faint)",
            }}
          >
            <span>Oldest</span>
            <span>Latest</span>
          </div>
        </Card>

        {/* Vector Convergence */}
        <Card style={{ padding: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600 }}>Vector Convergence</h3>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: "4px solid var(--c-primary)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                fontWeight: 600,
              }}
            >
              {(data.vectorConvergence * 100).toFixed(0)}%
            </div>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: 12,
              color: "var(--c-text-secondary)",
              margin: "8px 0 0",
            }}
          >
            {data.vectorConvergence >= 0.8
              ? "Well converged"
              : data.vectorConvergence >= 0.5
                ? "Converging"
                : "Still learning"}
          </p>
        </Card>
      </div>

      {/* Active Experiments */}
      <Card style={{ marginBottom: 20 }}>
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Active Experiments</h3>
        </div>
        {data.activeExperiments.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "var(--c-text-secondary)",
              fontSize: 13,
            }}
          >
            No active experiments for this workstream.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))" }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Experiment
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Improvement
                </th>
              </tr>
            </thead>
            <tbody>
              {data.activeExperiments.map((exp) => (
                <tr
                  key={exp.id}
                  style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.05))" }}
                >
                  <td style={{ padding: "8px 12px" }}>{exp.name}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <Badge
                      variant={
                        exp.status === "running"
                          ? "blue"
                          : exp.status === "completed"
                            ? "green"
                            : "neutral"
                      }
                    >
                      {exp.status}
                    </Badge>
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <span
                      style={{
                        color:
                          exp.improvement >= 0
                            ? "var(--c-success, #22c55e)"
                            : "var(--c-danger, #ef4444)",
                      }}
                    >
                      {exp.improvement >= 0 ? "+" : ""}
                      {(exp.improvement * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Contributing Tenants */}
      <Card>
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Contributing Tenants</h3>
        </div>
        {data.contributingTenants.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "var(--c-text-secondary)",
              fontSize: 13,
            }}
          >
            No contributing tenants yet.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))" }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Tenant
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Episodes
                </th>
              </tr>
            </thead>
            <tbody>
              {data.contributingTenants.map((tenant) => (
                <tr
                  key={tenant.id}
                  style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.05))" }}
                >
                  <td style={{ padding: "8px 12px" }}>
                    <Link
                      to={`/tenants/${tenant.id}`}
                      style={{ color: "var(--c-primary)", textDecoration: "none" }}
                    >
                      {tenant.name}
                    </Link>
                  </td>
                  <td style={{ padding: "8px 12px" }}>{tenant.episodes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
